<template>
  <div class="km-profile">
    <!-- ===== TOP BAR ===== -->
    <header class="kp-top">
      <button class="kp-btn-round" @click="back" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="kp-top__title">{{ displayName || 'Profile' }}</div>
      <button class="kp-btn-round" :class="{ active: liked }" @click="liked=!liked" aria-label="Like">
        <svg viewBox="0 0 24 24" width="20" height="20" :fill="liked?'#ff4d8d':'none'" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
      </button>
    </header>

    <!-- ===== HERO ===== -->
    <section class="kp-hero">
      <div class="kp-hero__img" :style="heroBg">
        <div v-if="gallery.length > 1" class="kp-hero__dots">
          <span v-for="(g,i) in gallery" :key="i" class="kp-dot" :class="{active: heroIdx===i}" @click="heroIdx=i"></span>
        </div>
      </div>
      <div class="kp-hero__info">
        <div class="kp-hero__row">
          <h1>{{ displayName }}<span v-if="xuanfeidata.age" class="kp-age">, {{ xuanfeidata.age }}</span></h1>
          <span v-if="online" class="kp-online">● Online</span>
        </div>
        <div class="kp-hero__sub">
          <span v-if="xuanfeidata.area" class="kp-meta">📍 {{ xuanfeidata.area }}</span>
          <span v-if="giftsReceived.length" class="kp-meta">🎁 {{ giftsReceived.length }} gifts received</span>
        </div>
        <p class="kp-bio" v-if="xuanfeidata.vo_title">{{ xuanfeidata.vo_title }}</p>

        <!-- Action buttons -->
        <div class="kp-actions">
          <button class="kp-btn kp-btn--gift" @click="openGiftPicker">
            <span class="kp-btn__emoji">🎁</span>
            Send a Gift
          </button>
        </div>
      </div>
    </section>

    <!-- ===== GIFTS RECEIVED WALL ===== -->
    <section v-if="giftsReceived.length" class="kp-section">
      <div class="kp-sec-head">
        <h3>🎁 Gifts received</h3>
        <small>{{ giftsReceived.length }} total</small>
      </div>
      <div class="kp-gift-wall">
        <div v-for="(g,i) in giftsReceived.slice(0,8)" :key="g.id || i" class="kp-gift-wall__item" :title="g.gift_name + ' ×' + g._count">
          <span class="kp-gift-wall__emoji">{{ g.emoji }}</span>
          <span class="kp-gift-wall__cnt" v-if="g._count>1">×{{ g._count }}</span>
        </div>
      </div>
    </section>

    <!-- ===== GALLERY ===== -->
    <section v-if="gallery.length" class="kp-section">
      <div class="kp-sec-head"><h3>📷 Photos</h3></div>
      <div class="kp-gallery">
        <div v-for="(g,i) in gallery" :key="'g'+i" class="kp-gallery__cell" @click="heroIdx=i">
          <img :src="g" @error="onImgErr" loading="lazy"/>
        </div>
      </div>
    </section>

    <!-- ===== FIXED BOTTOM CTA (mobile) ===== -->
    <div class="kp-sticky-cta">
      <button class="kp-btn kp-btn--gift kp-btn--lg" @click="openGiftPicker">
        <span class="kp-btn__emoji">🎁</span>
        Send {{ firstName }} a Gift
      </button>
    </div>

    <!-- ===== GIFT PICKER MODAL ===== -->
    <transition name="kp-fade">
      <div v-if="showGiftPicker" class="kp-modal" @click.self="closeGiftPicker">
        <div class="kp-sheet">
          <div class="kp-sheet__head">
            <div>
              <h3>Send a gift to {{ displayName }}</h3>
              <small>Your balance: <strong>{{ formatMoney(balance) }} PT</strong></small>
            </div>
            <button class="kp-close" @click="closeGiftPicker">×</button>
          </div>

          <!-- Loading / empty -->
          <div v-if="loadingGifts" class="kp-empty"><div class="kp-spin"></div> Loading gifts…</div>
          <div v-else-if="!gifts.length" class="kp-empty">🎁 No gifts available right now.</div>

          <!-- Grid -->
          <div v-else class="kp-gift-grid">
            <button
              v-for="g in gifts" :key="g.id"
              class="kp-gift-card"
              :class="{ selected: selectedGift && selectedGift.id===g.id, locked: Number(g.cost_pt) > balance }"
              @click="selectGift(g)"
            >
              <div class="kp-gift-card__emoji">{{ g.emoji }}</div>
              <div class="kp-gift-card__name">{{ g.name }}</div>
              <div class="kp-gift-card__cost">{{ formatMoney(g.cost_pt) }} PT</div>
              <span v-if="Number(g.cost_pt) > balance" class="kp-gift-card__lock">🔒</span>
            </button>
          </div>

          <!-- Message -->
          <div v-if="selectedGift" class="kp-message">
            <label>Add a note (optional)</label>
            <input v-model="giftMessage" maxlength="200" :placeholder="'Write something sweet to ' + firstName + '…'"/>
          </div>

          <!-- Footer -->
          <div class="kp-sheet__foot">
            <button class="kp-btn-ghost" @click="closeGiftPicker">Cancel</button>
            <button
              v-if="selectedGift && Number(selectedGift.cost_pt) <= balance"
              class="kp-btn kp-btn--gift"
              :disabled="sending"
              @click="sendGift"
            >
              <span v-if="sending"><span class="kp-spin kp-spin--sm"></span> Sending…</span>
              <span v-else>Send {{ selectedGift.emoji }} — {{ formatMoney(selectedGift.cost_pt) }} PT</span>
            </button>
            <button
              v-else-if="selectedGift"
              class="kp-btn kp-btn--topup"
              @click="goTopUp"
            >
              💎 Top up to send — need {{ formatMoney(selectedGift.cost_pt - balance) }} more PT
            </button>
            <button
              v-else
              class="kp-btn-ghost kp-btn--flex"
              disabled
            >Pick a gift above</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ===== SUCCESS TOAST ===== -->
    <transition name="kp-pop">
      <div v-if="successToast" class="kp-success">
        <div class="kp-success__emoji">{{ successToast.emoji }}</div>
        <div class="kp-success__body">
          <strong>Gift sent!</strong>
          <small>{{ successToast.name }} delivered to {{ displayName }}</small>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'KmProfile',
  data(){
    return {
      xuanfeidata: {},
      heroIdx: 0,
      liked: false,
      online: Math.random() > .35,

      // Gifts
      gifts: [],
      loadingGifts: false,
      showGiftPicker: false,
      selectedGift: null,
      giftMessage: '',
      sending: false,
      balance: 0,
      giftsReceived: [],
      successToast: null
    };
  },
  computed: {
    displayName(){
      return this.xuanfeidata.xuanfei_name
          || this.xuanfeidata.name
          || 'Profile';
    },
    firstName(){
      return String(this.displayName).split(/\s+/)[0];
    },
    gallery(){
      const u = this.xuanfeidata.img_url;
      if(Array.isArray(u)) return u;
      if(typeof u === 'string' && u) return u.split(',').map(s => s.trim()).filter(Boolean);
      return [];
    },
    heroBg(){
      const g = this.gallery;
      if(!g.length) return { background:'linear-gradient(135deg,#ff4d8d,#a020f0)' };
      return { backgroundImage: `url("${g[this.heroIdx % g.length]}")` };
    }
  },
  watch: {
    showGiftPicker(v){ document.body.style.overflow = v ? 'hidden' : ''; }
  },
  methods: {
    back(){ if(window.history.length > 1) this.$router.go(-1); else this.$router.push('/'); },
    onImgErr(e){ e.target.style.opacity = .25; },
    formatMoney(v){
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
    },

    getxuanfeidata(){
      this.$http({ method: 'get', url: 'xuanfeidata', data: { id: this.$route.query.id } })
        .then(res => { this.xuanfeidata = (res && res.data) || {}; });
    },
    loadBalance(){
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if(res && res.code === 200 && res.data){ this.balance = Number(res.data.money) || 0; }
      });
    },
    loadGifts(){
      this.loadingGifts = true;
      this.$http({ method: 'get', url: 'giftList' }).then(res => {
        this.loadingGifts = false;
        if(res && res.code === 200 && Array.isArray(res.data)){ this.gifts = res.data; }
      }).catch(() => { this.loadingGifts = false; });
    },
    loadGiftsReceived(){
      const id = this.$route.query.id;
      if(!id) return;
      this.$http({ method: 'post', url: 'giftsReceivedBy', data: { profile_id: id } }).then(res => {
        if(res && res.code === 200 && Array.isArray(res.data)){
          const map = {};
          res.data.forEach(g => {
            const key = (g.emoji || '?') + '|' + (g.gift_name || '');
            if(!map[key]) map[key] = Object.assign({}, g, { _count: 0 });
            map[key]._count += 1;
          });
          this.giftsReceived = Object.values(map);
        }
      });
    },

    openGiftPicker(){
      if(!localStorage.getItem('token')){
        this.$toast && this.$toast('Please log in to send gifts');
        this.$router.push({ path: '/Login', query: { redirect: this.$route.fullPath } });
        return;
      }
      this.showGiftPicker = true;
      this.selectedGift = null;
      this.giftMessage = '';
      if(!this.gifts.length) this.loadGifts();
      this.loadBalance();
    },
    closeGiftPicker(){ this.showGiftPicker = false; this.selectedGift = null; },
    selectGift(g){ this.selectedGift = g; },
    goTopUp(){ this.showGiftPicker = false; this.$router.push('/Deposit'); },
    sendGift(){
      if(!this.selectedGift || this.sending) return;
      this.sending = true;
      this.$http({
        method: 'post',
        url: 'giftSend',
        data: {
          gift_id: this.selectedGift.id,
          profile_id: this.$route.query.id,
          profile_name: this.displayName,
          message: this.giftMessage
        }
      }).then(res => {
        this.sending = false;
        if(res && res.code === 200){
          this.successToast = { emoji: this.selectedGift.emoji, name: this.selectedGift.name };
          setTimeout(() => { this.successToast = null; }, 2500);
          this.closeGiftPicker();
          this.loadBalance();
          this.loadGiftsReceived();
        } else if(res && res.code === 402){
          this.$toast && (this.$toast.fail ? this.$toast.fail(res.msg) : this.$toast(res.msg));
          setTimeout(() => this.goTopUp(), 900);
        } else {
          this.$toast && this.$toast((res && res.msg) || 'Could not send gift');
        }
      }).catch(() => { this.sending = false; this.$toast && this.$toast('Network error'); });
    }
  },
  created(){
    this.getxuanfeidata();
    this.loadGiftsReceived();
    if(localStorage.getItem('token')){
      this.loadBalance();
      this.loadGifts();
    }
  },
  beforeDestroy(){ document.body.style.overflow = ''; }
};
</script>

<style lang="less" scoped>
.km-profile{
  min-height: 100vh; padding-bottom: 120px;
  background: linear-gradient(180deg,#fdf5f9 0%,#f6eef3 100%);
  color:#2a1a3f;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.kp-top{
  position: sticky; top: 0; z-index: 40;
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 12px 14px;
  background: rgba(253,245,249,.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(160,32,240,.08);
}
.kp-btn-round{
  width:38px; height:38px; border-radius:50%;
  display:flex; align-items:center; justify-content:center;
  background:#fff; border:1px solid rgba(160,32,240,.12);
  color:#5a4a78; cursor:pointer; transition: all .15s;
}
.kp-btn-round:hover{ border-color:#ff4d8d; color:#ff4d8d; }
.kp-btn-round.active{ background: linear-gradient(135deg,#ff4d8d,#a020f0); color:#fff; border-color: transparent; }
.kp-top__title{ font-weight:800; font-size:15px; letter-spacing:-.01em; flex:1; text-align:center; }

.kp-hero{ padding: 14px; }
.kp-hero__img{
  width:100%; height: 58vw; max-height: 420px; border-radius: 20px;
  background-size: cover; background-position: center;
  box-shadow: 0 14px 36px rgba(74,26,110,.22);
  position: relative;
}
.kp-hero__dots{ position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); display:flex; gap:6px; }
.kp-dot{ width:6px; height:6px; border-radius:50%; background: rgba(255,255,255,.45); cursor:pointer; transition: width .2s; }
.kp-dot.active{ width: 20px; background:#fff; }
.kp-hero__info{
  background:#fff; margin-top: -20px; position: relative; z-index:1;
  border-radius:18px; padding: 18px 18px 14px;
  box-shadow: 0 10px 28px rgba(30,27,46,.06);
  border: 1px solid rgba(160,32,240,.08);
}
.kp-hero__row{ display:flex; align-items:center; justify-content:space-between; gap: 8px; }
.kp-hero__row h1{ margin:0; font-size:22px; font-weight:900; letter-spacing:-.02em; }
.kp-age{ color:#8273a0; font-weight:700; }
.kp-online{ font-size:10px; font-weight:800; letter-spacing:.05em; color:#10b981; text-transform:uppercase; }
.kp-hero__sub{ display:flex; gap: 14px; flex-wrap:wrap; margin-top:4px; font-size:12px; color:#6b6b82; }
.kp-bio{ margin: 10px 0 4px; font-size: 13px; color:#5a4a78; line-height:1.5; }

.kp-actions{ display:flex; gap:10px; margin-top: 14px; }
.kp-btn{
  flex: 1; padding: 12px; border-radius: 12px; border: 0; cursor:pointer;
  font-size: 14px; font-weight: 800; letter-spacing:.01em;
  display: inline-flex; align-items:center; justify-content:center; gap: 8px;
  font-family: inherit; color:#fff;
  transition: transform .15s, box-shadow .2s, background .15s;
}
.kp-btn:active{ transform: scale(.96); }
.kp-btn:disabled{ opacity: .6; cursor: not-allowed; }
.kp-btn--flex{ flex: 1; }
.kp-btn--gift{
  background: linear-gradient(135deg,#ff4d8d 0%,#a020f0 100%);
  box-shadow: 0 8px 22px rgba(255,77,141,.35);
}
.kp-btn--gift:hover:not(:disabled){ transform: translateY(-1px); box-shadow: 0 12px 28px rgba(255,77,141,.45); }
.kp-btn__emoji{ font-size: 18px; }
.kp-btn--lg{ padding: 14px; font-size: 15px; }
.kp-btn--topup{
  background: linear-gradient(135deg,#f59e0b,#d97706);
  box-shadow: 0 8px 22px rgba(245,158,11,.35);
}

.kp-section{ padding: 0 14px; margin-top: 18px; }
.kp-sec-head{ display:flex; align-items:baseline; justify-content:space-between; margin: 0 4px 10px; }
.kp-sec-head h3{ margin:0; font-size:13px; font-weight:800; color:#5a4a78; letter-spacing:.04em; text-transform:uppercase; }
.kp-sec-head small{ color:#8273a0; font-size:11px; }

.kp-gift-wall{ display:grid; grid-template-columns: repeat(4,1fr); gap:10px; }
.kp-gift-wall__item{
  position:relative; aspect-ratio: 1;
  background: linear-gradient(135deg,#fff7fb,#fbf1ff);
  border:1px solid rgba(160,32,240,.12); border-radius:14px;
  display:flex; align-items:center; justify-content:center;
  box-shadow: 0 4px 10px rgba(160,32,240,.08);
}
.kp-gift-wall__emoji{ font-size: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,.12)); }
.kp-gift-wall__cnt{
  position:absolute; top:4px; right:4px;
  background: linear-gradient(135deg,#ff4d8d,#a020f0); color:#fff;
  font-size:10px; font-weight:800; padding:2px 6px; border-radius:999px;
}

.kp-gallery{ display:grid; grid-template-columns: repeat(3,1fr); gap: 6px; }
.kp-gallery__cell{
  aspect-ratio: 1; border-radius: 12px; overflow:hidden;
  background: linear-gradient(135deg, rgba(255,77,141,.1), rgba(160,32,240,.1));
  cursor: pointer; transition: transform .15s;
}
.kp-gallery__cell:active{ transform: scale(.97); }
.kp-gallery__cell img{ width:100%; height:100%; object-fit:cover; display:block; }

.kp-sticky-cta{
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 30;
  padding: 12px 14px calc(12px + env(safe-area-inset-bottom,0));
  background: linear-gradient(180deg, rgba(253,245,249,0), rgba(253,245,249,.96) 40%);
}
.kp-sticky-cta .kp-btn{ width: 100%; max-width: 520px; margin: 0 auto; display:flex; }

.kp-modal{
  position: fixed; inset: 0; z-index: 60;
  background: rgba(26,16,51,.55); backdrop-filter: blur(4px);
  display: flex; align-items: flex-end; justify-content: center;
}
.kp-sheet{
  width: 100%; max-width: 520px;
  background: #fff; border-radius: 22px 22px 0 0;
  box-shadow: 0 -12px 40px rgba(26,16,51,.25);
  display: flex; flex-direction: column; max-height: 88vh;
  animation: kpSlideUp .3s cubic-bezier(.2,.8,.2,1);
}
@keyframes kpSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.kp-sheet__head{
  padding: 16px 18px;
  display:flex; align-items:flex-start; justify-content:space-between;
  border-bottom: 1px solid rgba(160,32,240,.08);
}
.kp-sheet__head h3{ margin:0; font-size: 15px; font-weight:900; }
.kp-sheet__head small{ color:#8273a0; font-size:12px; }
.kp-sheet__head strong{ color:#a020f0; }
.kp-close{
  width:30px; height:30px; border-radius:50%; border:0;
  background:#f6f0fa; color:#5a4a78; font-size:20px; line-height:26px; cursor:pointer;
}

.kp-gift-grid{
  overflow-y: auto; flex: 1; padding: 14px;
  display:grid; grid-template-columns: repeat(3,1fr); gap: 10px;
}
.kp-gift-card{
  position: relative;
  background: linear-gradient(135deg, #fff, #fffafd);
  border: 2px solid rgba(160,32,240,.08);
  border-radius: 14px;
  padding: 14px 8px 10px;
  text-align: center; cursor: pointer; font-family: inherit;
  transition: all .15s;
}
.kp-gift-card:hover{ border-color: rgba(255,77,141,.3); transform: translateY(-1px); }
.kp-gift-card.selected{
  border-color: #ff4d8d;
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  box-shadow: 0 6px 18px rgba(255,77,141,.25);
}
.kp-gift-card.locked{ opacity:.55; }
.kp-gift-card__emoji{ font-size: 34px; filter: drop-shadow(0 3px 6px rgba(0,0,0,.15)); }
.kp-gift-card__name{ margin-top: 6px; font-size: 12px; font-weight:800; color:#2a1a3f; }
.kp-gift-card__cost{ margin-top: 2px; font-size: 11px; font-weight:700; color:#a020f0; }
.kp-gift-card__lock{ position:absolute; top: 8px; right: 8px; font-size: 14px; }

.kp-message{ padding: 0 14px 10px; }
.kp-message label{
  display:block; font-size:11px; font-weight:800; color:#5a4a78; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: .05em;
}
.kp-message input{
  width:100%; padding: 11px 14px;
  border-radius: 12px; border: 1.5px solid rgba(160,32,240,.15);
  background: #faf7fd; font-size: 13px; color:#2a1a3f;
}
.kp-message input:focus{ outline:0; border-color:#ff4d8d; background:#fff; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }

.kp-sheet__foot{
  display:flex; gap: 10px; padding: 12px 14px calc(12px + env(safe-area-inset-bottom,0));
  border-top: 1px solid rgba(160,32,240,.08);
  background: #fafafd;
}
.kp-btn-ghost{
  flex: 0 0 auto; padding: 12px 16px;
  border-radius: 12px; border: 1px solid rgba(160,32,240,.15);
  background:#fff; color:#5a4a78; font-weight:800; font-size: 13px; cursor: pointer;
  font-family: inherit;
}
.kp-sheet__foot .kp-btn{ flex: 1; }

.kp-empty{ padding: 40px 20px; text-align:center; color:#8273a0; font-size:13px; display:flex; flex-direction:column; align-items:center; gap:8px; }
.kp-spin{ width:18px; height:18px; border-radius:50%; border:2px solid rgba(160,32,240,.2); border-top-color:#a020f0; animation: kpSpin .8s linear infinite; }
.kp-spin--sm{ width:12px; height:12px; display:inline-block; margin-right:4px; vertical-align:-1px; border-width:2px; border-top-color:#fff; }
@keyframes kpSpin { to { transform: rotate(360deg); } }

.kp-success{
  position: fixed; top: 80px; left: 50%; transform: translateX(-50%); z-index: 70;
  background:#fff; border-radius: 14px;
  box-shadow: 0 14px 38px rgba(26,16,51,.2);
  padding: 12px 18px;
  display:flex; align-items:center; gap: 12px;
  border: 1px solid rgba(160,32,240,.12);
}
.kp-success__emoji{ font-size: 32px; }
.kp-success__body strong{ display:block; font-size:14px; color:#2a1a3f; }
.kp-success__body small{ font-size:12px; color:#8273a0; }

.kp-fade-enter-active, .kp-fade-leave-active{ transition: opacity .22s ease; }
.kp-fade-enter, .kp-fade-leave-to{ opacity: 0; }
.kp-pop-enter-active, .kp-pop-leave-active{ transition: all .3s cubic-bezier(.2,.8,.2,1); }
.kp-pop-enter, .kp-pop-leave-to{ opacity: 0; transform: translateX(-50%) translateY(-10px) scale(.9); }

@media (min-width: 900px){
  .km-profile{ max-width: 680px; margin: 0 auto; }
  .kp-hero__img{ height: 420px; }
  .kp-hero{ padding: 20px; }
}
</style>
